// src/components/product/BrandSelectModal.tsx
import * as React from "react";
import { Modal, Table, Input, Typography, Empty } from "antd";
import type { ColumnsType } from "antd/es/table";
import { getBrandListApi } from "src/api/brand/brandApi";
import type { BrandListItem } from "src/api/brand/brandApi";
import CustomButton from "src/components/common/CustomButton";

type Props = {
  open: boolean;
  selectedId: number | null;
  onClose: () => void;
  onConfirm: (brandId: number | null, brandName: string | null) => void;
};

export default function BrandSelectModal({ open, selectedId, onClose, onConfirm }: Props) {
  const [list, setList] = React.useState<BrandListItem[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [page, setPage] = React.useState(0);
  const [size] = React.useState(10);
  const [totalElements, setTotalElements] = React.useState(0);
  const [searchValue, setSearchValue] = React.useState("");
  const [tempSelectedId, setTempSelectedId] = React.useState<number | null>(selectedId);

  React.useEffect(() => {
    if (open) {
      setTempSelectedId(selectedId);
      setPage(0);
      setSearchValue("");
    }
  }, [open, selectedId]);

  React.useEffect(() => {
    if (!open) return;
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, page, searchValue]);

  const fetchList = async () => {
    setLoading(true);
    try {
      const res = await getBrandListApi({
        brandName: searchValue || undefined,
        page,
        size,
        order: "brandNameKo,asc",
      });
      if (res.statusCode === 1 && res.data) {
        setList(res.data.content);
        setTotalElements(res.data.page.totalElements);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(0);
  };

  const handleConfirm = () => {
    if (tempSelectedId === null) {
      onConfirm(null, null);
    } else {
      const brand = list.find((b) => b.id === tempSelectedId);
      onConfirm(tempSelectedId, brand?.brandNameKo || null);
    }
    onClose();
  };

  const columns: ColumnsType<BrandListItem> = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 80,
      align: "center",
    },
    {
      title: "브랜드명 (한글)",
      dataIndex: "brandNameKo",
      key: "brandNameKo",
      width: 200,
    },
    {
      title: "브랜드명 (영문)",
      dataIndex: "brandNameEn",
      key: "brandNameEn",
      width: 200,
    },
    {
      title: "원산지",
      dataIndex: "originCountry",
      key: "originCountry",
      width: 120,
    },
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={800}
      centered
      title={<Typography.Text strong>브랜드 선택</Typography.Text>}
      footer={[
        <CustomButton key="cancel" variant="outlined" onClick={onClose}>
          취소
        </CustomButton>,
        <CustomButton key="clear" variant="outlined" onClick={() => setTempSelectedId(null)}>
          선택 해제
        </CustomButton>,
        <CustomButton key="confirm" variant="contained" onClick={handleConfirm}>
          확인
        </CustomButton>,
      ]}
    >
      <div style={{ marginBottom: 16 }}>
        <Input.Search
          placeholder="브랜드명 검색"
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          onSearch={handleSearch}
          style={{ width: 300 }}
        />
      </div>

      <Table
        columns={columns}
        dataSource={list}
        loading={loading}
        rowKey="id"
        pagination={{
          current: page + 1,
          pageSize: size,
          total: totalElements,
          onChange: (p) => setPage(p - 1),
          showSizeChanger: false,
        }}
        rowSelection={{
          type: "radio",
          selectedRowKeys: tempSelectedId !== null ? [tempSelectedId] : [],
          onChange: (selectedRowKeys) => {
            setTempSelectedId(selectedRowKeys[0] as number);
          },
        }}
        scroll={{ x: 1000 }}
        locale={{ emptyText: <Empty description="브랜드가 없습니다." /> }}
      />
    </Modal>
  );
}
